import { Button } from '@ads/components-react';
import {
  ChevronDownIcon,
  PencilSquareIcon,
  PlusIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/atoms';
import { useGradeConfig } from '../../../hooks/useGradeConfig';
import { sourceItem } from '../../../utils/gradeCalc';
import { moduleColorClass } from '../../../utils/moduleColors';
import { TypeTag } from '../../atoms/TypeTag';
import { SectionCard } from '../../molecules/SectionCard';

/**
 * Card "Módulos do curso": cada módulo é um accordion (cabeçalho com número, nome, peso
 * editável, contagem e link de edição) que expande para listar as disciplinas vinculadas.
 */
export function ModulesCard() {
  const { t } = useTranslation('gradeAttendanceConfig');

  const navigate = useNavigate();

  const { classGroup, modules, activities } = useGradeConfig();

  return (
    <SectionCard
      title={t('modules.title')}
      icon={<Squares2X2Icon className="size-xxs text-ctx-content-base" />}
      headerRight={
        <div className="flex items-center gap-xs">
          <span className="text-ctx-content-base tabular-nums ts-caption-xxs">
            {t('modules.meta', { count: modules.length })}
          </span>
          <Button
            onClick={() => {
              if (!classGroup) return;
              void navigate({
                to: '/class-modules/create',
                search: {
                  returnTo: 'grade-config',
                  classStudentId: Number(classGroup.id),
                  classStudentName: classGroup.name,
                },
              });
            }}
            leadingIcon={PlusIcon}
            hierarchy="secondary"
            size="sm"
            disabled={!classGroup}
          >
            {t('modules.add')}
          </Button>
        </div>
      }
    >
      <div className="px-xxxs py-mini">
        <Accordion type="multiple" className="mb-nano flex flex-col gap-nano">
          {modules.map((m, i) => {
            const links = activities.filter(a => a.moduleId === m.id);
            return (
              <AccordionItem
                key={m.id}
                value={m.id}
                className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body"
              >
                <div className="flex items-center gap-nano px-nano py-quark">
                  <AccordionTrigger className="!w-auto min-w-none flex-1 gap-nano">
                    <span
                      className={`inline-flex size-[1.125rem] shrink-0 items-center justify-center rounded-[0.3125rem] font-bold text-base-neutral-white ts-caption-xxs ${moduleColorClass(i)}`}
                    >
                      {i + 1}
                    </span>
                    <span className="truncate text-ctx-content-title ts-subtitle-xxs">
                      {m.name}
                    </span>
                    <ChevronDownIcon className="size-nano shrink-0 text-ctx-content-placeholder transition-transform group-data-[state=open]:rotate-180" />
                  </AccordionTrigger>

                  <span className="text-right text-ctx-content-placeholder tabular-nums ts-caption-xxs">
                    {t('modules.discCount', { count: links.length })}
                  </span>
                  <Button
                    onClick={() =>
                      void navigate({
                        to: '/class-modules/$id',
                        params: { id: m.id },
                        search: { returnTo: 'grade-config' },
                      })
                    }
                    leadingIcon={PencilSquareIcon}
                    hierarchy="tertiary"
                    size="sm"
                  >
                    {t('modules.edit')}
                  </Button>
                </div>

                {links.length > 0 && (
                  <AccordionContent>
                    <ul className="flex flex-col gap-nano border-t-hairline border-ctx-layout-border px-nano py-micro pl-[2rem]">
                      {links.map(a => (
                        <li key={a.id} className="flex items-center gap-nano">
                          <span className="truncate text-ctx-content-base ts-caption-xxs">
                            {(classGroup && sourceItem(classGroup, a)?.name) ?? '—'}
                          </span>
                          <TypeTag type={a.type} />
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>

        {modules.length === 0 && (
          <div className="px-xxxs py-mini text-center text-ctx-content-placeholder ts-caption-xxs">
            {t('modules.empty')}
          </div>
        )}
      </div>
    </SectionCard>
  );
}
